import { Select } from '@panwds/react-ui';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFilters } from '../../hooks/index.js';
import { FilterValueButton } from './FilterValueButton.js';
const emptyItems = [];
function toArray(itemOrItems) {
    return Array.isArray(itemOrItems)
        ? itemOrItems
        : itemOrItems
            ? [itemOrItems]
            : [];
}
function createCacheKey(name, selectedValue) {
    return `${name}:${selectedValue}`;
}
function selectedItemLabels({ selectedValues, items, cache, setCache, disableLabelCaching, name }) {
    const selectedItemLabels = [];
    const uncachedValues = new Set();
    const toBeCached = [];
    if (!disableLabelCaching) {
        // First we check the cache to see which values have cached labels.
        // Those that don't, we record so we can search the items.
        for (const selectedValue of selectedValues) {
            const label = cache.get(createCacheKey(name, selectedValue));
            if (label)
                selectedItemLabels.push(label);
            else
                uncachedValues.add(selectedValue);
        }
        // If there were values with uncached labels, we check the items for them.
        // We take the children prop first but fall back to the value itself.
        if (uncachedValues.size) {
            for (const item of items) {
                if (uncachedValues.has(item.value)) {
                    const label = item.children ?? item.value;
                    toBeCached.push([createCacheKey(name, item.value), label]);
                    uncachedValues.delete(item.value);
                    selectedItemLabels.push(label);
                }
                if (!uncachedValues.size)
                    break;
            }
        }
        // In the rare case there are any values remaining, that means they have been
        // removed from the list of items. In this case we fall back to using the
        // values as labels. We also cache them to save time on future re-renders.
        if (uncachedValues.size) {
            for (const value of uncachedValues.values()) {
                selectedItemLabels.push(value);
                toBeCached.push([createCacheKey(name, value), value]);
            }
        }
    }
    else {
        for (const selectedValue of selectedValues) {
            const item = items.find((item) => item.value === selectedValue);
            const label = item?.children ?? item?.value;
            selectedItemLabels.push(label);
        }
    }
    useEffect(() => {
        if (toBeCached?.length)
            setCache(toBeCached);
    }, [setCache, toBeCached]);
    return selectedItemLabels;
}
/**
 * Provides a Select that will set the filter state for the name given
 * to this component.
 *
 * **Note**: If you have a lot of items in your list (10k+) it may start to slow down.
 * Use StaticSelectFilter or paginate depending on your use case.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectFilter } from "@panwds/react-filters";
 * ```
 */
export function SelectFilter({ autoFocus, name, items = emptyItems, label, onClose, disabled = false, addClassName, addButtonClassName, buttonRef, id = 'select-filter', enableMultiSelect = false, enableSearch = false, disableLabelCaching = false, ...rest }) {
    const intl = useIntl();
    const { filters, setFilters, cache, setCache } = useFilters();
    const handleOnChange = useCallback((changes) => {
        setCache([[createCacheKey(name, changes.selectedItem.value), changes.selectedItem.children || undefined]]);
        if (enableMultiSelect) {
            const filteredItemValues = items.map((item) => item.value);
            const selectedValues = filters[name] || [];
            // When this component is controlled, we don't want to lose selected
            // filter values that are not present in items that may have been filtered.
            const selectedItemsNotInFilteredList = Array.isArray(selectedValues)
                ? selectedValues.filter((value) => !filteredItemValues.includes(value))
                : [];
            setFilters({
                [name]: [...changes.selectedItems.map((item) => item.value), ...selectedItemsNotInFilteredList]
            });
        }
        else {
            setFilters({
                [name]: `${changes.selectedItem.value}`
            });
        }
    }, [items, filters, setFilters, enableMultiSelect]);
    const selectedItem = useMemo(() => {
        if (enableMultiSelect) {
            return items.filter((item) => {
                const values = filters[name];
                return Array.isArray(values) && values.includes(item.value);
            });
        }
        return items.find((item) => item.value === filters[name]);
    }, [items, filters[name], enableMultiSelect]);
    // TODO: fix filter value type
    const values = toArray(filters[name]);
    const labels = selectedItemLabels({
        selectedValues: values,
        items,
        cache,
        setCache,
        disableLabelCaching,
        name
    });
    const select = (React.createElement("div", { id: id },
        React.createElement(Select, { ...rest, disabled: disabled, addClassName: addClassName, items: items, buttonRef: buttonRef, enableSearch: enableSearch, enableMultiSelect: enableMultiSelect, button: React.createElement(FilterValueButton, { autoFocus: autoFocus, enableSelectAll: rest.enableSelectAll, selectedItemLabels: labels, items: items, label: intl.formatMessage({
                    defaultMessage: '{label}',
                    id: 'I4bSrq',
                    description: 'Label on select when nothing has been selected yet'
                }, {
                    label: label
                }), name: name, disabled: disabled, onClose: onClose, required: rest.required, isFullWidth: rest.isFullWidth, addClassName: addButtonClassName, placeholder: rest.placeholder, ...rest }), onChange: handleOnChange, selectedItem: selectedItem })));
    return select;
}
//# sourceMappingURL=SelectFilter.js.map